import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeamMoments } from '../teamMoments';
import API from '../services/api';

function TeamCard({ teamMembers = [] }) {
  const navigate = useNavigate();
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  const [shoutData, setShoutData] = useState({ items: [], unseenCount: 0 });

  useEffect(() => {
    let active = true;
    API.get('/therapist/shout-outs')
      .then((r) => { if (active) setShoutData({ items: r.data.items || [], unseenCount: r.data.unseenCount || 0 }); })
      .catch(() => { /* non-fatal: card just omits shout-out slides */ });
    return () => { active = false; };
  }, []);

  const [promptSlide, setPromptSlide] = useState(null);

  useEffect(() => {
    let active = true;
    API.get('/therapist/team-prompt')
      .then((r) => {
        if (active && r.data.prompt) {
          setPromptSlide({ text: r.data.prompt.text, count: (r.data.answers || []).length });
        }
      })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  const moments = useMemo(() => getTeamMoments(teamMembers, { windowDays: 30 }), [teamMembers]);
  const acceptingCount = useMemo(
    () => teamMembers.filter((m) => m.accepting_clients).length,
    [teamMembers]
  );

  // Build rotating slides: shout-outs first, then moments, then a summary slide.
  const slides = useMemo(() => {
    const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    const shoutSlides = (shoutData.items || []).slice(0, 5).map((so) => ({
      key: `s-${so.id}`,
      emoji: '💜',
      title: `${so.fromName} → ${so.toName}`,
      sub: so.message.length > 80 ? `${so.message.slice(0, 80)}…` : so.message,
    }));
    const momentSlides = moments.map((mo) => {
      const name = mo.profile.first_name || 'Teammate';
      if (mo.type === 'birthday') {
        return {
          key: `b-${mo.profile.therapist_id}`,
          emoji: '🎂',
          title: `${name}'s birthday`,
          sub: mo.daysAway === 0 ? 'today! 🎉' : fmt(mo.date),
        };
      }
      return {
        key: `a-${mo.profile.therapist_id}`,
        emoji: '🎉',
        title: `${name} — ${mo.years === 0 ? 'first year!' : `${mo.years} year${mo.years === 1 ? '' : 's'}`} at the practice`,
        sub: mo.daysAway === 0 ? 'today!' : fmt(mo.date),
      };
    });
    const promptSlides = promptSlide
      ? [{
          key: 'prompt',
          emoji: '✨',
          title: 'This week',
          sub: promptSlide.text.length > 80 ? `${promptSlide.text.slice(0, 80)}…` : promptSlide.text,
        }]
      : [];
    return [
      ...shoutSlides,
      ...promptSlides,
      ...momentSlides,
      {
        key: 'summary',
        emoji: '👥',
        title: `${teamMembers.length} teammate${teamMembers.length === 1 ? '' : 's'}`,
        sub: `${acceptingCount} accepting new clients`,
      },
    ];
  }, [shoutData, promptSlide, moments, teamMembers.length, acceptingCount]);

  // Auto-advance every 5s, paused on hover, only when more than one slide.
  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const id = setInterval(() => setSlide((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [paused, slides.length]);

  // Keep the index valid if the slide set shrinks.
  useEffect(() => {
    setSlide((i) => (i >= slides.length ? 0 : i));
  }, [slides.length]);

  const activeIndex = slides.length ? slide % slides.length : 0;
  const current = slides[activeIndex] || null;

  const MAX_AVATARS = 9;
  const shown = teamMembers.slice(0, MAX_AVATARS);
  const extra = teamMembers.length - shown.length;

  return (
    <div style={teamCardStyle} onClick={() => navigate('/therapist/team')}>
      <div style={cardHeaderStyle}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Your Team</span>
          {shoutData.unseenCount > 0 && (
            <span style={badgeStyle}>💜 {shoutData.unseenCount} new</span>
          )}
        </div>
      </div>

      <div style={cardBodyStyle}>
        {teamMembers.length === 0 ? (
          <div style={emptyStateStyle}><p>No team members found</p></div>
        ) : (
          <>
            <div style={avatarStripStyle}>
              {shown.map((m, i) => (
                <div key={m.therapist_id || i} style={{ ...avatarWrapStyle, marginLeft: i === 0 ? 0 : -8, zIndex: shown.length - i }}>
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={`${m.first_name || ''} ${m.last_name || ''}`} style={avatarImgStyle} />
                  ) : (
                    <div style={{ ...avatarImgStyle, ...avatarFallbackStyle }}>
                      {(m.first_name?.[0] || '')}{(m.last_name?.[0] || '')}
                    </div>
                  )}
                </div>
              ))}
              {extra > 0 && (
                <div style={{ ...avatarWrapStyle, marginLeft: -8, zIndex: 0 }}>
                  <div style={{ ...avatarImgStyle, ...avatarFallbackStyle, background: '#1D2C5F', color: '#fff' }}>+{extra}</div>
                </div>
              )}
            </div>

            {current && (
              <div style={spotlightRowStyle}>
                {slides.length > 1 && (
                  <button
                    type="button"
                    aria-label="Previous"
                    onClick={(e) => { e.stopPropagation(); setSlide((i) => (i - 1 + slides.length) % slides.length); }}
                    style={arrowButtonStyle}
                  >
                    ‹
                  </button>
                )}
                <div
                  style={spotlightStyle}
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                >
                  <div style={{ fontSize: 26, lineHeight: 1 }}>{current.emoji}</div>
                  <div style={spotlightTitleStyle}>{current.title}</div>
                  <div style={spotlightSubStyle}>{current.sub}</div>
                </div>
                {slides.length > 1 && (
                  <button
                    type="button"
                    aria-label="Next"
                    onClick={(e) => { e.stopPropagation(); setSlide((i) => (i + 1) % slides.length); }}
                    style={arrowButtonStyle}
                  >
                    ›
                  </button>
                )}
              </div>
            )}

            {slides.length > 1 && (
              <div style={dotsRowStyle}>
                {slides.map((s, i) => (
                  <span
                    key={s.key}
                    onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                    style={{ ...dotStyle, background: i === activeIndex ? '#1D2C5F' : '#cbd5e1' }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <p style={viewTeamTextStyle}>Click to open the Team Hub →</p>
      </div>
    </div>
  );
}

const teamCardStyle = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 1px 8px rgba(0,0,0,0.09)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  height: '100%',
};
const cardHeaderStyle = { background: '#1D2C5F', padding: '14px 20px' };
const badgeStyle = {
  background: '#fff',
  color: '#1D2C5F',
  borderRadius: 999,
  padding: '2px 9px',
  fontSize: 11,
  fontWeight: 700,
};
const cardBodyStyle = {
  padding: '1.25rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
};
const avatarStripStyle = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  marginBottom: 14,
};
const avatarWrapStyle = { position: 'relative' };
const avatarImgStyle = {
  width: 34,
  height: 34,
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  display: 'block',
};
const avatarFallbackStyle = {
  backgroundColor: '#e0e0e0',
  color: '#555',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
};
const spotlightRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
};
const arrowButtonStyle = {
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '1px solid #d0d5e0',
  background: '#fff',
  color: '#1D2C5F',
  fontSize: 20,
  lineHeight: 1,
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const spotlightStyle = {
  flex: 1,
  minWidth: 0,
  boxSizing: 'border-box',
  background: 'linear-gradient(135deg, #1D2C5F 0%, #3A4F9E 100%)',
  color: '#fff',
  borderRadius: 12,
  padding: '16px 14px',
  textAlign: 'center',
  minHeight: 92,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
};
const spotlightTitleStyle = { fontWeight: 700, fontSize: 14, marginTop: 4, lineHeight: 1.25 };
const spotlightSubStyle = { fontSize: 12, opacity: 0.85 };
const dotsRowStyle = { display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10 };
const dotStyle = { width: 7, height: 7, borderRadius: '50%', cursor: 'pointer', display: 'inline-block' };
const emptyStateStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 0',
  color: '#999',
  flex: 1,
};
const viewTeamTextStyle = {
  fontSize: '0.9rem',
  color: '#878DA1',
  fontWeight: 600,
  margin: 'auto 0 0 0',
  textAlign: 'center',
  padding: '0.5rem 0',
};

export default TeamCard;
